/**
 * `/__ui/tasks` — the fixture harness for M10's web half (the same trick B4's board harness and
 * round 5m's collab harness use).
 *
 * It renders with **no session, no api and no database**, so a served build can be hit-tested
 * by `tools/browser/verify-tasks-local.mjs` at three viewports and by `e2e/tests/tasks.spec.ts`
 * without a login. Six surfaces, selected by `?screen=`:
 *
 *   `mine` (default) · `assigned` · `archive` · `insights` · `timeline` · `empty`
 *
 * Round **T4** added the last two of the five tabs. T2 left them out because the panels were
 * another round's and read through tRPC; T3 shipped them with `source?` as a parameter, so the
 * fixture can now drive all five tabs — which is what the phone work needs, because *"open every
 * Tasks tab on a phone and screenshot it"* cannot be done with two of them unreachable.
 *
 * **P-319** adds the seventh surface, and it is a *route* rather than a `?screen=`:
 * `/__ui/tasks/pulses/<itemId>` is the harness's copy of the product's `/tasks/pulses/<itemId>`,
 * registered as a sibling of `/__ui/tasks` with the same element so that opening and closing the
 * card is the same history gesture here as there — which is the thing the verifier has to be able
 * to prove without a session (the list stays mounted, Escape returns to the list, the selection
 * survives).
 */
import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { createFixtureItemCardSource } from '../item-card/fixture';
import { TasksPage } from './TasksPage';
import { createFixtureInsightsSource, createFixtureTasksSource } from './fixture';
import type { TaskPageTab } from './types';
import { taskItemIdFromPath } from './vocabulary';
import './tasks.css';

const SCREEN_TABS: Record<string, TaskPageTab> = {
  mine: 'mine',
  assigned: 'assigned',
  archive: 'archive',
  insights: 'insights',
  timeline: 'timeline',
};

/** The harness's own base path, so the `?screen=` query survives opening and closing a card. */
const HARNESS_PATH = '/__ui/tasks';

export function TasksHarness() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search);
  const screen = params.get('screen') ?? 'mine';
  const empty = screen === 'empty';
  const source = useMemo(() => createFixtureTasksSource({ empty }), [empty]);
  const insightsSource = useMemo(() => createFixtureInsightsSource({ empty }), [empty]);
  const [tab, setTab] = useState<TaskPageTab>(SCREEN_TABS[screen] ?? 'mine');

  /** P-319 · the open item comes from the URL here exactly as it does on the product route. */
  const openItemId = taskItemIdFromPath(location.pathname);
  /**
   * One fixture card per item, so the panel's header names the item that was clicked rather than
   * the fixture's default. It is rebuilt when the item changes and never otherwise, because the
   * card reloads its feed whenever its source identity moves.
   */
  const cardSource = useMemo(
    () => (openItemId === null ? null : createFixtureItemCardSource({ itemId: openItemId })),
    [openItemId],
  );
  const search = location.search;

  return (
    <div
      data-testid="ui-tasks"
      style={{ minHeight: '100vh', background: 'var(--primary-background-color)' }}
    >
      <TasksPage
        source={source}
        insightsSource={insightsSource}
        tab={tab}
        onTabChange={setTab}
        openItemId={openItemId}
        itemCardSource={cardSource}
        onOpenItem={(ref) =>
          /* P-322: a card already open is replaced, never stacked in the history. */
          navigate(`${HARNESS_PATH}/pulses/${ref.itemId}${search}`, { replace: openItemId !== null })
        }
        onCloseItem={() => {
          /* The product route's rule (F4 §7.1 b): go back when the entry is ours, else replace. */
          if (window.history.state?.idx > 0) navigate(-1);
          else navigate(`${HARNESS_PATH}${search}`, { replace: true });
        }}
        /* The harness has no board screen to go to; the link is still drawn and hit-tested. */
        onOpenBoard={() => undefined}
      />
    </div>
  );
}
